import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/store'
import { selectFitGapSummary, selectRequirementsTotalEffort } from '../slices/requirementsSlice'
import type { RequirementFitGapVerdict } from '../types'

const VERDICT_ORDER: readonly RequirementFitGapVerdict[] = ['STANDARD', 'LIBRARY', 'NEW', 'UNCLASSIFIED']

const SEGMENT_BAR: Record<RequirementFitGapVerdict, string> = {
  STANDARD: 'bg-done',
  LIBRARY: 'bg-mint',
  NEW: 'bg-neutral',
  UNCLASSIFIED: 'bg-review'
}

/**
 * AC-2/AC-3 — the fit-gap summary and the total effort figure, both DERIVED
 * (`selectFitGapSummary` / `selectRequirementsTotalEffort`, `createSelector`
 * over the loaded rows). Reads the slice directly rather than taking props,
 * so a verdict swap elsewhere on the page moves this bar in the same render
 * with no additional request (INV-2.3).
 */
export function FitGapSummary() {
  const { t } = useTranslation('projectWorkspace')
  const summary = useAppSelector(selectFitGapSummary)
  const totalEffort = useAppSelector(selectRequirementsTotalEffort)

  const segments = VERDICT_ORDER.map((verdict) => ({
    verdict,
    count: summary.byVerdict[verdict],
    pct: summary.total === 0 ? 0 : Math.round((summary.byVerdict[verdict] / summary.total) * 100)
  })).filter((segment) => segment.count > 0)

  return (
    <section
      aria-labelledby="fit-gap-heading"
      className="rounded-lg border border-border bg-accent px-4 py-4"
    >
      <SectionLabel>{t('requirements.summary.label')}</SectionLabel>
      <h2 id="fit-gap-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
        {t('requirements.summary.heading')}
      </h2>

      {summary.total === 0 ? (
        <p className="mt-3 text-[13px] text-ink-soft">{t('requirements.summary.empty')}</p>
      ) : (
        <>
          <div
            className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-card"
            role="img"
            aria-label={`${t('requirements.summary.barLabel')}: ${segments
              .map((s) => `${t(`requirements.verdict.${s.verdict}`)} ${s.pct}%`)
              .join(', ')}`}
          >
            {segments.map((s) => (
              <span
                key={s.verdict}
                className={cn('block h-full', SEGMENT_BAR[s.verdict])}
                style={{ width: `${s.pct}%` }}
              />
            ))}
          </div>

          <ul className="mt-3">
            {segments.map((s) => (
              <li key={s.verdict} className="flex items-baseline justify-between gap-2 py-1">
                <span className="flex items-center gap-1.5 text-[13px] text-foreground">
                  <span className={cn('h-2 w-2 rounded-sm', SEGMENT_BAR[s.verdict])} aria-hidden />
                  {t(`requirements.verdict.${s.verdict}`)}
                </span>
                <span className="v-figure text-[13px] tabular-nums text-ink-soft">
                  {t('requirements.summary.segmentLine', { count: s.count, pct: s.pct })}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="mt-2 border-t border-mint/30 pt-2.5 text-xs text-muted-foreground">
        {totalEffort === null
          ? t('requirements.effort.notDisclosed')
          : t('requirements.effort.total', { hours: totalEffort })}
      </p>
    </section>
  )
}
